import { ArrowRightOutlined, FormOutlined } from '@ant-design/icons';
import { Alert, Empty, Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type WorkflowSummary, workflowsApi } from '@/api/workflows';

const { Title, Text, Paragraph } = Typography;

/**
 * Landing page for the pretend-advisor flow. Lists every published Smart Form
 * the advisor can start an application on and provides a per-row "Start new
 * application" button. Clicking navigates to `/advisor/workflows/:id/
 * applications/new`, which mints an applicationId and forwards to the
 * ID-scoped route (see `AdvisorApplicationStartPage`).
 *
 * TODO(BMO-ADVISOR-INTEGRATION): SWAP POINTS
 *
 *   1. This page shouldn't exist under real BMO integration — the advisor
 *      arrives from the BMO Advisor Portal with a specific Smart Form
 *      already selected. Keep it around as a dev-login-only affordance for
 *      QA / demo runs, gated by `isDevLoginEnabled()` in the route wiring
 *      once BMO handoff lands.
 *   2. The list currently pulls `workflowsApi.list()` — the ORIGINATOR
 *      list endpoint. Real advisor path will want a slimmed advisor-facing
 *      list (audience-filtered, no draft/rejected workflows exposed).
 *   3. The client-side `Approved + isActive` filter is a stand-in for
 *      whatever server-side filter the real endpoint will apply.
 */
export function AdvisorDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<WorkflowSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await workflowsApi.list();
        if (cancelled) return;
        // Advisor only sees Smart Forms that are Approved AND currently
        // published (`isActive`). Drafts / pending / rejected / archived
        // aren't advisor-facing. See swap-point 2 above — this filter
        // moves server-side under real integration.
        const eligible = list.filter((w) => w.status === 'Approved' && w.isActive === true);
        setWorkflows(eligible);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Title level={3} style={{ margin: 0 }}>
        {t('advisor.dashboard.title')}
      </Title>
      <Alert
        showIcon
        type="info"
        message={t('advisor.dashboard.pretendBannerTitle')}
        description={t('advisor.dashboard.pretendBannerBody')}
      />
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : loadError ? (
        <Alert type="error" message={loadError} />
      ) : !workflows || workflows.length === 0 ? (
        <Empty description={t('advisor.dashboard.emptyState')} />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 20,
          }}
        >
          {workflows.map((w) => (
            <SmartFormCard
              key={w.id}
              workflow={w}
              onStart={() => navigate(`/advisor/workflows/${w.id}/applications/new`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Selectable Smart Form tile. Whole card acts as a button so keyboard + touch
 * users get one target instead of a hunt for the CTA — the top-left icon
 * tile is the visual anchor, the arrow chip at the bottom-right is the
 * affordance, hover elevates the whole card. Internal appName / template id
 * is intentionally NOT surfaced — advisors don't need our technical
 * identifiers cluttering the pick.
 *
 * We use `<div role="button">` rather than a real `<button>` so we can nest
 * `<Paragraph ellipsis>` (renders as `<div>`) inside without triggering
 * validateDOMNesting — the HTML spec only allows phrasing content inside
 * `<button>`. Keyboard behavior is manually implemented via `onKeyDown` so
 * Enter + Space still fire the click.
 */
function SmartFormCard({ workflow, onStart }: { workflow: WorkflowSummary; onStart: () => void }) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const name =
    (isFr ? workflow.nameFr : workflow.nameEn) ||
    workflow.nameEn ||
    workflow.appName ||
    t('advisor.dashboard.untitledForm');
  const description = (isFr ? workflow.descriptionFr : workflow.descriptionEn) || '';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onStart}
      onKeyDown={(e) => {
        // Enter + Space are the two keys native `<button>` responds to;
        // mirroring both keeps parity with keyboard-only advisors.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStart();
        }
      }}
      className="bmo-advisor-card"
      aria-label={t('advisor.dashboard.startApplicationAria', { name })}
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 16,
        // Softer than a typical shadow — kicks in on hover via the class.
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        padding: 24,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        // Smoothes the hover transition so the elevation change isn't jarring.
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        minHeight: 200,
      }}
    >
      <div
        style={{
          // Icon tile — BMO-blue-tinted rounded square, gives every card an
          // identical visual anchor no matter how long the workflow name is.
          width: 44,
          height: 44,
          borderRadius: 12,
          background: 'linear-gradient(135deg, #eaf4fb 0%, #cfe4f4 100%)',
          color: '#0179C1',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        <FormOutlined />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minHeight: 0 }}>
        <Text
          strong
          style={{
            fontSize: 16,
            color: '#0f172a',
            lineHeight: 1.35,
            letterSpacing: -0.1,
          }}
        >
          {name}
        </Text>
        {description && (
          <Paragraph
            type="secondary"
            style={{ marginBottom: 0, fontSize: 13, lineHeight: 1.5 }}
            ellipsis={{ rows: 3 }}
          >
            {description}
          </Paragraph>
        )}
      </div>
      <div
        style={{
          marginTop: 'auto',
          paddingTop: 12,
          borderTop: '1px solid #f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <Text
          style={{
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: '#64748b',
          }}
        >
          {workflow.currentVersionLabel
            ? // Advisor only sees Approved+Active workflows, so a version label
              // is always present — falling back to just "Smart Form" if a
              // published workflow ever ships without a label is defensive but
              // not user-facing today.
              t('advisor.dashboard.cardMetaWithVersion', {
                version: workflow.currentVersionLabel,
              })
            : t('advisor.dashboard.cardMeta')}
        </Text>
        <span
          className="bmo-advisor-card__cta"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 999,
            background: '#0179C1',
            color: '#fff',
            fontSize: 13,
            fontWeight: 500,
            transition: 'background 160ms ease, transform 160ms ease',
          }}
        >
          {t('advisor.dashboard.startApplication')}
          <ArrowRightOutlined style={{ fontSize: 12 }} />
        </span>
      </div>
    </div>
  );
}
